/**
 * 새 학생 창(요청서 S3) — 학생 앱 로그인 계정(로그인 이메일 · 비밀번호)과 이름 · 연락처 · 출결번호 + 학교·학년 + 부서를 저장한다
 * (백엔드 2단계 전달 §4-A). 맨 위에서 둘 중 하나를 고른다(백엔드 2b 전달 §3-A · 오너 결정 2026-10-13):
 *   학생 정보만(처음 값) — 등록일 · 반 칸이 없다. 상태 「등록 전」, 수강이 정해지면 상세에서 [등록]한다(설명회 때 계정을 먼저 만드는 흐름).
 *   반 등록까지 — 등록일(필수)과 반까지 한 번에. 오늘보다 뒤면 등록 예정.
 * 등록일 칸을 비우는 것으로 정보만을 고르지 않는다 — 비운 실수로 정보만 학생이 생기지 않게.
 * 왼쪽 학생 표 머리의 [새 학생](매니저만)이 연다. 저장하면 새 학생 상세로 간다.
 *
 * 고른 반의 입반일은 등록일 — 반을 만든 날보다 앞이면 반을 만든 날부터. 한 반이라도 안 되면 학생도 만들어지지 않는다(백엔드 4단계 전달 §3).
 * 칸 규칙(이름 · 이메일 · 비밀번호 · 연락처 형식, 겹침)은 서버가 본다 — 창은 규칙을 안내하고, 거절은 서버 문구 그대로 보인다.
 */
import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { createStudent, listDepartments } from '../academyApi';
import { schoolLevelOfGrade, todayKST } from '../periodDates';
import { SegTabs, useAsync } from '../ui';
import TestStudentField from './TestStudentField';
import {
  DateInput, Field, FormModal, MultiSelectInput, NAME_RULE, Note, PASSWORD_RULE, PasswordInput, SelectInput, StudentNoInput, TextInput,
  gradeOptionsForSchool, schoolOptions, studentNoHint, studentNoOk, useClassList, useSchools,
} from './form';

const MODES = [['info', '학생 정보만'], ['register', '반 등록까지']];

const EMPTY = {
  name: '', email: '', password: '', studentNo: '', mobile: '', parent: '', startOn: '', schoolId: '', grade: '', deptId: '', classIds: [], isTest: false,
};

function CreateStudentModal({ onClose, onCreated }) {
  const { getAccessToken, userType } = useAuth();
  const schools = useSchools();
  const classes = useClassList('open');
  const depts = useAsync((signal) => listDepartments({ signal }, getAccessToken), 'depts-create');
  const [mode, setMode] = useState('info');
  const register = mode === 'register';
  const [f, setF] = useState(() => ({ ...EMPTY, startOn: todayKST() }));
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const selectedSchool = schools.find((s) => String(s.school_id) === f.schoolId);
  const pickSchool = (schoolId) => {
    const options = gradeOptionsForSchool(schools.find((s) => String(s.school_id) === schoolId));
    setF((prev) => ({ ...prev, schoolId, grade: options.some(([value]) => value === prev.grade) ? prev.grade : '' }));
  };
  // 학년을 고르면 학교급이 같은 부서를 미리 고른다(바꿀 수 있음). 13 졸업 · 14 비재학은 학교급이 없어 고르지 않는다
  const pickGrade = (v) => {
    const level = schoolLevelOfGrade(Number(v));
    const hint = level && (depts.data || []).find((d) => !d.archived && d.school_levels.includes(level));
    setF((prev) => ({ ...prev, grade: v, deptId: prev.deptId || (hint ? String(hint.department_id) : '') }));
  };
  return (
    <FormModal
      onClose={onClose}
      title="새 학생"
      size="lg"
      submitLabel={register ? '등록' : '학생 저장'}
      top={<div className="flex"><SegTabs tabs={MODES} value={mode} onChange={setMode} ariaLabel="새 학생 저장 방식" /></div>}
      canSubmit={!!f.name.trim() && !!f.email.trim() && !!f.password && studentNoOk(f.studentNo) && (!register || !!f.startOn) && !!f.grade}
      onSubmit={async () => {
        // 정보만이면 등록일 · 반을 보내지 않는다(createStudent 가 start_on 키를 뺀다 — 빈 글자는 400)
        const d = await createStudent({
          studentName: f.name.trim(), studentEmail: f.email.trim(), password: f.password, studentNo: f.studentNo,
          mobileNumber: f.mobile.trim(), parentMobileNumber: f.parent.trim(),
          startOn: register ? f.startOn : null, schoolId: f.schoolId ? Number(f.schoolId) : null, grade: Number(f.grade),
          departmentId: f.deptId ? Number(f.deptId) : null, classIds: register ? f.classIds : [],
          ...(userType === 'manager' ? { isTest: f.isTest } : {}),
        }, getAccessToken);
        if (d?.student_id && onCreated) onCreated(d.student_id);
      }}
    >
      {!register && <Note>등록하지 않고 학생 정보만 저장합니다. 상태는 「등록 전」이며, 수강이 정해지면 상세에서 [등록]합니다.</Note>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="이름" required hint={NAME_RULE}><TextInput value={f.name} onChange={set('name')} autoComplete="off" /></Field>
        <Field label="로그인 이메일" required>
          <TextInput value={f.email} onChange={set('email')} placeholder="로그인 이메일" inputMode="email" autoComplete="off" />
        </Field>
        <Field label="비밀번호" required hint={PASSWORD_RULE}><PasswordInput value={f.password} onChange={set('password')} /></Field>
        <Field label="출결번호" hint={studentNoHint(f.studentNo)}><StudentNoInput value={f.studentNo} onChange={set('studentNo')} placeholder="선택" /></Field>
        <Field label="학생 연락처"><TextInput value={f.mobile} onChange={set('mobile')} placeholder="010-0000-0000(선택)" inputMode="tel" /></Field>
        <Field label="학부모 연락처"><TextInput value={f.parent} onChange={set('parent')} placeholder="010-0000-0000(선택)" inputMode="tel" /></Field>
        {register && <Field label="등록일" required hint="오늘보다 뒤면 등록 예정."><DateInput value={f.startOn} onChange={set('startOn')} /></Field>}
        <Field label="학교">
          <SelectInput value={f.schoolId} onChange={pickSchool} options={schoolOptions(schools)} placeholder="학교 미지정" />
        </Field>
        <Field label="학년" required><SelectInput value={f.grade} onChange={pickGrade} options={gradeOptionsForSchool(selectedSchool, f.grade)} placeholder="학년 선택" /></Field>
        <Field label="부서"><SelectInput value={f.deptId} onChange={set('deptId')} options={(depts.data || []).map((d) => [d.department_id, d.name])} placeholder="부서 미지정" /></Field>
      </div>
      {userType === 'manager' && <TestStudentField checked={f.isTest} onChange={set('isTest')} />}
      {register && <Field label="입반할 반" hint="여러 반을 선택할 수 있습니다.">
        <MultiSelectInput
          aria-label="입반할 반"
          value={f.classIds}
          onChange={set('classIds')}
          options={classes.map((c) => [c.class_id, c.name])}
          placeholder="반 선택"
          emptyText="활성 반이 없습니다."
        />
      </Field>}
    </FormModal>
  );
}

export default CreateStudentModal;
